
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel, Stack, Tooltip } from '@mui/material';
import {
  AccountBalanceOutlined as BankIcon,
  AccountBalanceWalletOutlined as HeadMasterIcon,
  AppsOutlined as AppsIcon,
  CalendarMonthOutlined as CalendarIcon,
  EditOutlined as EditIcon,
  GavelOutlined as TaxMasterIcon,
  HourglassTopOutlined as PendingIcon,
  Inventory2Outlined as ArticlesIcon,
  MonetizationOnOutlined as MoneyIcon,
  PeopleAltOutlined as UsersIcon,
  PersonAddAltOutlined as AddUserIcon,
  ReceiptLongOutlined as RtgsIcon,
} from '@mui/icons-material';
import { ColorLensOutlined as PaletteIcon, BorderColorOutlined as BorderIcon, Circle as CircleIcon, FormatColorText as TextIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Layout, { getAllSidebarPageItems } from '../components/Layout';
import { useAuth, hasPageAccess } from '../context/AuthContext';
import { API_BASE_URL } from '../apiBase';

function toHex(n) {
  const s = Number(n || 0).toString(16);
  return s.length === 1 ? '0' + s : s;
}
function rgbaToHex(v) {
  try {
    const m = String(v || '').match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (!m) return null;
    const r = toHex(m[1]);
    const g = toHex(m[2]);
    const b = toHex(m[3]);
    return `#${r}${g}${b}`;
  } catch {
    return null;
  }
}
function valueForPicker(v, fallback = '#000000') {
  if (!v) return fallback;
  if (String(v).startsWith('#')) return v;
  const hex = rgbaToHex(v);
  return hex || fallback;
}

function StatCard({ title, value, linkText, icon, gradient, onClick }) {
  return (
    <Paper
      onClick={onClick}
      sx={{
        p: 3,
        height: 150,
        borderRadius: 2.5,
        position: 'relative',
        color: 'white',
        background: gradient,
        cursor: onClick ? 'pointer' : 'default',
        boxShadow: '0 12px 20px rgba(0,0,0,0.08)',
        overflow: 'hidden',
      }}
    >
      <Typography variant="subtitle2" sx={{ opacity: 0.92 }}>
        {title}
      </Typography>
      <Typography variant="h3" sx={{ mt: 1, fontWeight: 500, lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography
        variant="body2"
        sx={{ mt: 2, textDecoration: 'underline', opacity: 0.95, display: 'inline-block' }}
      >
        {linkText}
      </Typography>
      <Box sx={{ position: 'absolute', right: 16, bottom: 10, fontSize: 86, opacity: 0.16 }}>
        {icon}
      </Box>
    </Paper>
  );
}

function QuickActionCard({ title, subtitle, icon, color, bg, borderColor, badge, textColor, iconColor, onClick }) {
  return (
    <Paper
      onClick={onClick}
      sx={{
        p: 2.5,
        borderRadius: 2.5,
        border: '2px solid',
        borderColor,
        bgcolor: bg,
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        transition: 'transform 120ms ease, box-shadow 120ms ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 20px rgba(0,0,0,0.08)' },
        height: 150,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        overflow: 'hidden',
      }}
    >
      {typeof badge === 'number' && badge > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: -10,
            right: -10,
            width: 26,
            height: 26,
            borderRadius: '50%',
            bgcolor: 'error.main',
            color: 'error.contrastText',
            display: 'grid',
            placeItems: 'center',
            fontSize: 12,
            fontWeight: 800,
            border: '2px solid',
            borderColor: 'background.paper',
          }}
        >
          {badge}
        </Box>
      )}
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          bgcolor: color,
          color: iconColor || 'white',
          mx: 'auto',
          mb: 1.25,
        }}
      >
        {icon}
      </Box>
      <Typography align="center" sx={{ fontWeight: 500, px: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: textColor || 'inherit' }}>
        {title}
      </Typography>
      <Typography align="center" variant="body2" sx={{ color: textColor || 'text.secondary', mt: 0.5, px: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {subtitle}
      </Typography>
    </Paper>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const canAccess = (pageKey) => hasPageAccess(user, pageKey);

  const allSidebarItems = useMemo(() => getAllSidebarPageItems(), []);
  const extraItems = useMemo(
    () =>
      [
        { key: 'bank_master', label: 'Bank Master', to: '/bank-master', icon: <BankIcon />, pageKey: 'bank_master' },
        { key: 'head_master', label: 'Head Master', to: '/head-master', icon: <HeadMasterIcon />, pageKey: 'head_master' },
        { key: 'tax_master', label: 'Tax Master', to: '/tax-master', icon: <TaxMasterIcon />, pageKey: 'tax_master' },
      ].filter((it) => canAccess(it.pageKey)),
    [user]
  );
  const availableActions = useMemo(() => {
    const items = [...allSidebarItems.filter((it) => canAccess(it.pageKey || it.key)), ...extraItems];
    const seen = new Set();
    const unique = [];
    for (const it of items) {
      if (seen.has(it.to)) continue;
      seen.add(it.to);
      unique.push(it);
    }
    return unique;
  }, [allSidebarItems, extraItems, user]);

  const colorMap = useMemo(
    () => ({
      users: { color: '#2563eb', bg: 'rgba(37,99,235,0.08)', borderColor: 'rgba(37,99,235,0.35)' },
      edit_requests: { color: '#f97316', bg: 'rgba(249,115,22,0.08)', borderColor: 'rgba(249,115,22,0.35)' },
      applications: { color: '#16a34a', bg: 'rgba(22,163,74,0.08)', borderColor: 'rgba(22,163,74,0.35)' },
      applications_add: { color: '#7c3aed', bg: 'rgba(124,58,237,0.08)', borderColor: 'rgba(124,58,237,0.35)' },
      article_creation: { color: '#0ea5e9', bg: 'rgba(14,165,233,0.08)', borderColor: 'rgba(14,165,233,0.35)' },
      vendor_master: { color: '#0891b2', bg: 'rgba(8,145,178,0.08)', borderColor: 'rgba(8,145,178,0.35)' },
      vendor_followup: { color: '#0f766e', bg: 'rgba(15,118,110,0.08)', borderColor: 'rgba(15,118,110,0.35)' },
      vendor_followup_report: { color: '#7c3aed', bg: 'rgba(124,58,237,0.08)', borderColor: 'rgba(124,58,237,0.35)' },
      head_office_report: { color: '#0ea5e9', bg: 'rgba(14,165,233,0.08)', borderColor: 'rgba(14,165,233,0.35)' },
      manual_head_office_transaction: { color: '#10b981', bg: 'rgba(16,185,129,0.08)', borderColor: 'rgba(16,185,129,0.35)' },
      code_to_code_purpose: { color: '#16a34a', bg: 'rgba(22,163,74,0.08)', borderColor: 'rgba(22,163,74,0.35)' },
      transaction_types: { color: '#10b981', bg: 'rgba(16,185,129,0.08)', borderColor: 'rgba(16,185,129,0.35)' },
      rtgs: { color: '#0ea5e9', bg: 'rgba(14,165,233,0.08)', borderColor: 'rgba(14,165,233,0.35)' },
      calendar: { color: '#db2777', bg: 'rgba(219,39,119,0.08)', borderColor: 'rgba(219,39,119,0.35)' },
      bank_master: { color: '#6366f1', bg: 'rgba(99,102,241,0.08)', borderColor: 'rgba(99,102,241,0.35)' },
      head_master: { color: '#ef4444', bg: 'rgba(239,68,68,0.08)', borderColor: 'rgba(239,68,68,0.35)' },
      tax_master: { color: '#0f766e', bg: 'rgba(15,118,110,0.08)', borderColor: 'rgba(15,118,110,0.35)' },
      default: { color: '#7c3aed', bg: 'rgba(124,58,237,0.08)', borderColor: 'rgba(124,58,237,0.35)' },
    }),
    []
  );

  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [openCustomize, setOpenCustomize] = useState(false);
  const [stylesByRoute, setStylesByRoute] = useState({});
  const [editStyles, setEditStyles] = useState({});
  const [activeCustomizeRoute, setActiveCustomizeRoute] = useState(null);
  const [hasUnsavedEdits, setHasUnsavedEdits] = useState(false);

  useEffect(() => {
    if (openCustomize && !activeCustomizeRoute && availableActions.length > 0) {
      setActiveCustomizeRoute(availableActions[0].to);
    }
  }, [openCustomize, availableActions, activeCustomizeRoute]);

  useEffect(() => {
    const fetchLayout = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/quick-action-layout`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        });
        const data = await res.json();
        if (Array.isArray(data?.routes)) {
          setSelectedRoutes(data.routes);
          return;
        }
      } catch (e) {
        console.error("Failed to load layout:", e);
      }

      // Fallback to defaults if no DB layout found
      const defaults = [
        '/users',
        '/requests',
        '/applications',
        '/applications/add',
        '/edit-requests',
        '/articles',
        '/vendors',
        '/vendor-followup',
        '/vendor-followup-report',
        '/head-office-report',
        '/manual-head-office-transaction',
        '/code-purpose',
        '/transaction-types',
        '/rtgs',
        '/calendar',
        '/bank-master',
        '/head-master',
        '/tax-master',
      ].filter((to) => availableActions.find((it) => it.to === to));
      
      const uniq = [];
      const seen = new Set();
      for (const to of defaults) {
        if (seen.has(to)) continue;
        seen.add(to);
        uniq.push(to);
      }
      setSelectedRoutes(uniq);
    };

    if (user) {
      fetchLayout();
    }
  }, [user, availableActions]);

  useEffect(() => {
    const loadStyles = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/quick-action-styles`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        });
        const data = await res.json();
        const arr = Array.isArray(data?.styles) ? data.styles : [];
        const map = {};
        arr.forEach((s) => {
          map[s.route] = {
            cardBgColor: s.cardBgColor || null,
            borderColor: s.borderColor || null,
            iconBgColor: s.iconBgColor || null,
            iconColor: s.iconColor || null,
            textColor: s.textColor || null,
          };
        });
        setStylesByRoute(map);
        if (!hasUnsavedEdits && !openCustomize && Object.keys(editStyles || {}).length === 0) {
          setEditStyles(map);
        }
      } catch {}
    };
    loadStyles();
  }, [user, hasUnsavedEdits, openCustomize, editStyles]);

  const savePreferences = async (next) => {
    // Optimistic update
    setSelectedRoutes(next);
    
    // Save to DB
    try {
      await fetch(`${API_BASE_URL}/api/quick-action-layout`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({ routes: next }),
      });
    } catch (e) {
      console.error("Failed to save layout:", e);
    }
  };

  const onDragStart = (e, index) => {
    try {
      e.dataTransfer.setData('text/plain', String(index));
    } catch {}
  };

  const onDragOver = (e) => {
    e.preventDefault();
    try {
      e.dataTransfer.dropEffect = 'move';
    } catch {}
  };

  const onDrop = (e, targetIndex) => {
    e.preventDefault();
    let sourceIndex = null;
    try {
      sourceIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    } catch {}
    if (sourceIndex == null || isNaN(sourceIndex)) return;
    if (sourceIndex === targetIndex) return;
    const next = selectedRoutes.slice();
    const [moved] = next.splice(sourceIndex, 1);
    next.splice(targetIndex, 0, moved);
    savePreferences(next);
  };

  const toggleRoute = (to, checked) => {
    const exists = selectedRoutes.includes(to);
    let next = selectedRoutes.slice();
    if (checked && !exists) {
      next.push(to);
    } else if (!checked && exists) {
      next = next.filter((r) => r !== to);
    }
    savePreferences(next);
  };

  const resetDefaults = () => {
    const next = availableActions.map((it) => it.to);
    savePreferences(next);
  };

  const mergedStyleFor = (route, pageKey) => {
    const s = editStyles[route] || stylesByRoute[route] || {};
    const theme = colorMap[pageKey] || colorMap.default;
    return {
      cardBgColor: s.cardBgColor || theme.bg,
      borderColor: s.borderColor || theme.borderColor,
      iconBgColor: s.iconBgColor || theme.color,
      iconColor: s.iconColor || 'white',
      textColor: s.textColor || undefined,
    };
  };

  const setEditStyle = (route, key, value) => {
    setEditStyles((prev) => ({
      ...prev,
      [route]: {
        ...(prev[route] || {}),
        [key]: value,
      },
    }));
    setHasUnsavedEdits(true);
  };

  const saveStylesToServer = async () => {
    try {
      setStylesByRoute((prev) => ({ ...prev, ...editStyles }));
      const stylesPayload = selectedRoutes.map((route) => {
        const item = availableActions.find((it) => it.to === route);
        const pageKey = item?.pageKey;
        const theme = colorMap[pageKey] || colorMap.default;
        const s = editStyles[route] || {};
        return {
          route,
          cardBgColor: s.cardBgColor || theme.bg,
          borderColor: s.borderColor || theme.borderColor,
          iconBgColor: s.iconBgColor || theme.color,
          iconColor: s.iconColor || 'white',
          textColor: s.textColor || null,
        };
      });
      const res = await fetch(`${API_BASE_URL}/api/quick-action-styles`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({ styles: stylesPayload }),
      });
      const ok = res.ok;
      if (ok) {
        setHasUnsavedEdits(false);
      }
      setOpenCustomize(false);
    } catch {
      setStylesByRoute((prev) => ({ ...prev, ...editStyles }));
      setOpenCustomize(false);
    }
  };

  return (
    <Layout>
      {/* Top stats */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 2.5,
        }}
      >
        <StatCard
          title="Total Users"
          value="5"
          linkText="View entire list"
          icon={<UsersIcon sx={{ fontSize: 'inherit' }} />}
          gradient="linear-gradient(90deg,#22c55e,#10b981)"
          onClick={() => navigate('/users')}
        />
        <StatCard
          title="Pending Requests from Users"
          value="3"
          linkText="View entire list"
          icon={<PendingIcon sx={{ fontSize: 'inherit' }} />}
          gradient="linear-gradient(90deg,#f59e0b,#fbbf24)"
          onClick={() => navigate('/requests')}
        />
        <StatCard
          title="User Applications"
          value="15"
          linkText="View entire list"
          icon={<AppsIcon sx={{ fontSize: 'inherit' }} />}
          gradient="linear-gradient(90deg,#8b5cf6,#7c3aed)"
          onClick={() => navigate('/applications')}
        />
        <StatCard
          title="Financial Status"
          value="6"
          linkText="View RTGS Details"
          icon={<MoneyIcon sx={{ fontSize: 'inherit' }} />}
          gradient="linear-gradient(90deg,#3b82f6,#2563eb)"
          onClick={() => navigate('/rtgs')}
        />
      </Box>

      {/* Quick actions */}
      <Box sx={{ mt: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            Quick Actions
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Frequently used actions
            </Typography>
            <Button variant="outlined" size="small" onClick={() => setOpenCustomize(true)}>
              Customize
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
            gap: 2.5,
          }}
        >
          {selectedRoutes.map((to, idx) => {
            const item = availableActions.find((it) => it.to === to);
            if (!item) return null;
            const { label, pageKey, icon } = item;
            const style = mergedStyleFor(to, pageKey);
            return (
              <Box
                key={to}
                draggable
                onDragStart={(e) => onDragStart(e, idx)}
                onDragOver={onDragOver}
                onDrop={(e) => onDrop(e, idx)}
              >
                <QuickActionCard
                  title={label}
                  subtitle=""
                  icon={icon}
                  color={style.iconBgColor}
                  bg={style.cardBgColor}
                  borderColor={style.borderColor}
                  iconColor={style.iconColor}
                  textColor={style.textColor}
                  onClick={() => navigate(to)}
                />
              </Box>
            );
          })}
        </Box>
      </Box>
      <Dialog open={openCustomize} onClose={() => setOpenCustomize(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 2 }}>Customize Quick Actions</DialogTitle>
        <DialogContent sx={{ p: 0, height: 500, display: 'flex', overflow: 'hidden' }}>
          {/* Left Sidebar: List */}
          <Box sx={{ width: '35%', borderRight: '1px solid #eee', overflowY: 'auto', bgcolor: '#f8fafc' }}>
            {availableActions.map((it) => {
              const route = it.to;
              const selected = selectedRoutes.includes(route);
              const isActive = activeCustomizeRoute === route;
              return (
                <Box
                  key={route}
                  onClick={() => setActiveCustomizeRoute(route)}
                  sx={{
                    p: 2,
                    borderBottom: '1px solid #eee',
                    cursor: 'pointer',
                    bgcolor: isActive ? 'white' : 'transparent',
                    borderLeft: isActive ? '4px solid #2563eb' : '4px solid transparent',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    '&:hover': { bgcolor: isActive ? 'white' : '#f1f5f9' }
                  }}
                >
                  <Checkbox
                    checked={selected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => toggleRoute(route, e.target.checked)}
                    size="small"
                  />
                  <Box sx={{ overflow: 'hidden' }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: isActive ? 600 : 400, color: isActive ? 'text.primary' : 'text.secondary' }}>
                      {it.label}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Right Panel: Preview & Controls */}
          <Box sx={{ width: '65%', p: 3, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            {(() => {
              const activeItem = availableActions.find(it => it.to === activeCustomizeRoute);
              if (!activeItem) {
                return (
                   <Box sx={{ display: 'grid', placeItems: 'center', height: '100%', color: 'text.secondary' }}>
                    Select an item to customize
                  </Box>
                );
              }
              
              const activeStyle = mergedStyleFor(activeItem.to, activeItem.pageKey);
              const activeEditStyle = editStyles[activeItem.to] || {};
              const previewStyle = {
                cardBgColor: activeEditStyle.cardBgColor || activeStyle.cardBgColor,
                borderColor: activeEditStyle.borderColor || activeStyle.borderColor,
                iconBgColor: activeEditStyle.iconBgColor || activeStyle.iconBgColor,
                iconColor: activeEditStyle.iconColor || activeStyle.iconColor,
                textColor: activeEditStyle.textColor || activeStyle.textColor,
              };

              return (
              <>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Preview</Typography>
                <Box sx={{ width: 240, mx: 'auto', mb: 4 }}>
                  <QuickActionCard
                    title={activeItem.label}
                    subtitle="Preview Mode"
                    icon={activeItem.icon}
                    color={previewStyle.iconBgColor}
                    bg={previewStyle.cardBgColor}
                    borderColor={previewStyle.borderColor}
                    iconColor={previewStyle.iconColor}
                    textColor={previewStyle.textColor}
                  />
                </Box>

                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Style Settings</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                   {/* Card Background */}
                   <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>Card Background</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid #e2e8f0', p: 1, borderRadius: 1 }}>
                        <input
                          type="color"
                          value={valueForPicker(previewStyle.cardBgColor)}
                          onChange={(e) => setEditStyle(activeItem.to, 'cardBgColor', e.target.value)}
                          style={{ width: 32, height: 32, padding: 0, border: 'none', borderRadius: 4, cursor: 'pointer' }}
                        />
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {valueForPicker(previewStyle.cardBgColor)}
                        </Typography>
                      </Box>
                   </Box>

                   {/* Border Color */}
                   <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>Border Color</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid #e2e8f0', p: 1, borderRadius: 1 }}>
                        <input
                          type="color"
                          value={valueForPicker(previewStyle.borderColor)}
                          onChange={(e) => setEditStyle(activeItem.to, 'borderColor', e.target.value)}
                          style={{ width: 32, height: 32, padding: 0, border: 'none', borderRadius: 4, cursor: 'pointer' }}
                        />
                         <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {valueForPicker(previewStyle.borderColor)}
                        </Typography>
                      </Box>
                   </Box>

                   {/* Icon Background */}
                   <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>Icon Background</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid #e2e8f0', p: 1, borderRadius: 1 }}>
                        <input
                          type="color"
                          value={valueForPicker(previewStyle.iconBgColor)}
                          onChange={(e) => setEditStyle(activeItem.to, 'iconBgColor', e.target.value)}
                          style={{ width: 32, height: 32, padding: 0, border: 'none', borderRadius: 4, cursor: 'pointer' }}
                        />
                         <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {valueForPicker(previewStyle.iconBgColor)}
                        </Typography>
                      </Box>
                   </Box>

                   {/* Icon Color */}
                   <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>Icon Color</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid #e2e8f0', p: 1, borderRadius: 1 }}>
                        <input
                          type="color"
                          value={valueForPicker(previewStyle.iconColor)}
                          onChange={(e) => setEditStyle(activeItem.to, 'iconColor', e.target.value)}
                          style={{ width: 32, height: 32, padding: 0, border: 'none', borderRadius: 4, cursor: 'pointer' }}
                        />
                         <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {valueForPicker(previewStyle.iconColor)}
                        </Typography>
                      </Box>
                   </Box>

                   {/* Text Color */}
                   <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>Text Color</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid #e2e8f0', p: 1, borderRadius: 1 }}>
                        <input
                          type="color"
                          value={valueForPicker(previewStyle.textColor || '#000000')}
                          onChange={(e) => setEditStyle(activeItem.to, 'textColor', e.target.value)}
                          style={{ width: 32, height: 32, padding: 0, border: 'none', borderRadius: 4, cursor: 'pointer' }}
                        />
                         <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {valueForPicker(previewStyle.textColor || '#000000')}
                        </Typography>
                      </Box>
                   </Box>
                </Box>
              </>
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #eee' }}>
          <Button onClick={resetDefaults}>Select All</Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setOpenCustomize(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveStylesToServer}>Save Changes</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
